import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    createFlowAngleNoise,
    type FlowAngleNoiseMode,
} from "../../utils/flowAngleNoise";
import {
    buildFlowGrid,
    quantizeAngleRad,
    sampleAngle,
    sampleStepWeight,
    type FlowFieldModeOptions,
    type FlowGrid,
    type Point2,
} from "../../utils/flowField";
import { computeFlowFieldDebugSegments } from "../../utils/flowFieldDebugSegments";
import {
    advancePeriodicSpawnTimer,
    applyStrokeWidthKick,
    applyVelocityKick,
    clampPointInsideBounds,
    isOutsideOrOnEdge,
    randomPointInDisk,
    resolveAgentStrokeColor,
    sampleKickDelay,
    sampleTrailLengthCap,
    trailToSvgFragments,
    unitPerpendicularToMotion,
    computeChildTrailLengthCap,
    type ChildTrailCapMode,
    type ViewBoxBounds,
} from "../../utils/pathAgents";
import { buildFlowAgentsGroupUnion } from "../../utils/flowAgentsUnion";
import {
    type AgentInputMode,
    DEFAULT_AGENT_INPUT_MODE,
} from "../../utils/agentInputMode";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export type { ChildTrailCapMode };

export interface FlowFollowAgent {
    id: string;
    spawnGroupId: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    points: Point2[];
    strokeWidths: number[];
    liveStrokeWidth: number;
    strokeColor: string;
    arcLength: number;
    /** Sampled at spawn from max trail length ± randomness; arc length stops here. */
    trailLengthCap: number;
    /** Followers steer toward this agent id each frame. */
    leaderId?: string;
    isGroupLeader?: boolean;
    /** Remaining frames of gradual velocity kick; optional per-frame impulse below. */
    kickBlendRemaining?: number;
    kickPerFrameVx?: number;
    kickPerFrameVy?: number;
    strokeWidthBlendRemaining?: number;
    strokeWidthPerFrameW?: number;
    /** Seconds until this agent may spawn another agent at its position. */
    childSpawnTimerSec?: number;
    /** Kinematic multi-stroke: follows leader each frame; no independent physics. */
    parallelStrokeLeaderId?: string;
    parallelStrokeSlotIndex?: number;
    parallelStrokeSlaveIds?: string[];
}

export interface CompletedFlowAgentPath {
    id: string;
    spawnGroupId: string;
    points: Point2[];
    strokeWidths?: number[];
    strokeColor: string;
}

export interface CompletedFlowUnionGroup {
    spawnGroupId: string;
    pathD: string;
    fill: string;
}

interface FlowAgentsState {
    width: number;
    height: number;
    marginFactor: number;
    cellSize: number;
    seed: string;
    angleNoiseMode: FlowAngleNoiseMode;
    angleNoiseOctaves: number;
    angleWarpStrength: number;
    angleWarpFrequency: number;
    noiseScale: number;
    stepNoiseScale: number;
    stepVariation: number;
    angleQuantizeDeg: number;
    fieldRotationDeg: number;
    mirrorNoiseX: boolean;
    mirrorNoiseY: boolean;
    uniformFlowBlend: number;
    uniformFlowAngleDeg: number;
    showFlowField: boolean;
    followSpeed: number;
    /** 0 = velocity ignores the field; 1 = velocity steers strongly toward the field direction. */
    flowInfluence: number;
    maxTrailLength: number;
    /** 0 = each agent uses `maxTrailLength`; 1 = uniform ±100% of base (clamped). */
    maxTrailLengthRandomness: number;
    strokeColor: string;
    strokePaletteText: string;
    setStrokePaletteText: (v: string) => void;
    randomStrokeColorPerAgent: boolean;
    setRandomStrokeColorPerAgent: (v: boolean) => void;
    strokeWidth: number;
    minStrokeWidth: number;
    initialStrokeWidth: number;
    strokeWidthStep: number;
    strokeWidthKickFrames: number;
    strokeWidthKickGradual: boolean;
    strokeWidthBlendFrames: number;
    /** Fade stroke width over this arc length at trail start/end (SVG user units). */
    trailTaperStartLength: number;
    trailTaperEndLength: number;
    smoothPaths: boolean;
    mergeTrailToSinglePath: boolean;
    filledVariableWidthStroke: boolean;
    unionGroupOnComplete: boolean;
    backgroundColor: string;
    exportBackground: boolean;
    groupSize: number;
    groupRadius: number;
    /** Parallel stroke origins per group position (multiplies with agents per click). */
    strokeLayoutCount: number;
    strokeLayoutSpacing: number;
    groupCohesionStrength: number;
    kickIntervalSec: number;
    kickIntervalRandomnessSec: number;
    forceMagnitude: number;
    velocityKickGradual: boolean;
    velocityKickBlendFrames: number;
    inputMode: AgentInputMode;
    setInputMode: (v: AgentInputMode) => void;
    setPuppetBrushTarget: (
        spawnGroupId: string,
        x: number,
        y: number,
    ) => void;
    clearPuppetBrush: () => void;
    setWidth: (v: number) => void;
    setHeight: (v: number) => void;
    setMarginFactor: (v: number) => void;
    setCellSize: (v: number) => void;
    setSeed: (v: string) => void;
    setAngleNoiseMode: (v: FlowAngleNoiseMode) => void;
    setAngleNoiseOctaves: (v: number) => void;
    setAngleWarpStrength: (v: number) => void;
    setAngleWarpFrequency: (v: number) => void;
    setNoiseScale: (v: number) => void;
    setStepNoiseScale: (v: number) => void;
    setStepVariation: (v: number) => void;
    setAngleQuantizeDeg: (v: number) => void;
    setFieldRotationDeg: (v: number) => void;
    setMirrorNoiseX: (v: boolean) => void;
    setMirrorNoiseY: (v: boolean) => void;
    setUniformFlowBlend: (v: number) => void;
    setUniformFlowAngleDeg: (v: number) => void;
    setShowFlowField: (v: boolean) => void;
    setFollowSpeed: (v: number) => void;
    setFlowInfluence: (v: number) => void;
    setMaxTrailLength: (v: number) => void;
    setMaxTrailLengthRandomness: (v: number) => void;
    setStrokeColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setMinStrokeWidth: (v: number) => void;
    setInitialStrokeWidth: (v: number) => void;
    setStrokeWidthStep: (v: number) => void;
    setStrokeWidthKickFrames: (v: number) => void;
    setStrokeWidthKickGradual: (v: boolean) => void;
    setStrokeWidthBlendFrames: (v: number) => void;
    setTrailTaperStartLength: (v: number) => void;
    setTrailTaperEndLength: (v: number) => void;
    setSmoothPaths: (v: boolean) => void;
    setMergeTrailToSinglePath: (v: boolean) => void;
    setFilledVariableWidthStroke: (v: boolean) => void;
    setUnionGroupOnComplete: (v: boolean) => void;
    setBackgroundColor: (v: string) => void;
    setExportBackground: (v: boolean) => void;
    setGroupSize: (v: number) => void;
    setGroupRadius: (v: number) => void;
    setStrokeLayoutCount: (v: number) => void;
    setStrokeLayoutSpacing: (v: number) => void;
    setGroupCohesionStrength: (v: number) => void;
    setKickIntervalSec: (v: number) => void;
    setKickIntervalRandomnessSec: (v: number) => void;
    /** Seconds between spawns at the agent position; 0 = off. */
    spawnChildIntervalSec: number;
    setSpawnChildIntervalSec: (v: number) => void;
    /** ± seconds jitter for the next spawn delay. */
    spawnChildIntervalRandomnessSec: number;
    setSpawnChildIntervalRandomnessSec: (v: number) => void;
    childTrailCapMode: ChildTrailCapMode;
    setChildTrailCapMode: (v: ChildTrailCapMode) => void;
    childTrailCapFixed: number;
    setChildTrailCapFixed: (v: number) => void;
    childTrailCapRandomness: number;
    setChildTrailCapRandomness: (v: number) => void;
    /** When true (default), agents finish when they leave the artboard. */
    stopAtMargin: boolean;
    setStopAtMargin: (v: boolean) => void;
    setForceMagnitude: (v: number) => void;
    setVelocityKickGradual: (v: boolean) => void;
    setVelocityKickBlendFrames: (v: number) => void;
    viewBoxAttr: string;
    debugSegments: { x1: number; y1: number; x2: number; y2: number }[];
    activeAgents: FlowFollowAgent[];
    completedPaths: CompletedFlowAgentPath[];
    completedUnionGroups: CompletedFlowUnionGroup[];
    /**
     * Optional drag vector (SVG user space): same heading for the whole spawn; speed magnitude from field at spawn center.
     * Returns `spawnGroupId` when agents were added.
     */
    spawnAgentAt: (
        x: number,
        y: number,
        initialDir?: { dx: number; dy: number } | null,
        options?: SpawnAgentAtOptions,
    ) => string | undefined;
    clearTrails: () => void;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    randomizeParameters: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const FlowAgentsContext = createContext<FlowAgentsState | null>(null);

export function useFlowAgents() {
    const ctx = useContext(FlowAgentsContext);
    if (!ctx)
        throw new Error("useFlowAgents must be used within FlowAgentsProvider");
    return ctx;
}

const DEFAULT_FLOW_AGENTS_PRESET: Record<string, unknown> = {
    width: 800,
    height: 600,
    marginFactor: 0.5,
    cellSize: 10,
    seed: "flow-agents-1",
    angleNoiseMode: "perlin",
    angleNoiseOctaves: 4,
    angleWarpStrength: 0.35,
    angleWarpFrequency: 1,
    noiseScale: 0.08,
    stepNoiseScale: 0.09,
    stepVariation: 0,
    angleQuantizeDeg: 0,
    fieldRotationDeg: 0,
    mirrorNoiseX: false,
    mirrorNoiseY: false,
    uniformFlowBlend: 0,
    uniformFlowAngleDeg: 0,
    followSpeed: 180,
    flowInfluence: 1,
    maxTrailLength: 2400,
    maxTrailLengthRandomness: 0,
    strokeColor: "#2a3d4a",
    strokePaletteText: "",
    randomStrokeColorPerAgent: false,
    strokeWidth: 3,
    minStrokeWidth: 0.25,
    initialStrokeWidth: 0.45,
    strokeWidthStep: 0.5,
    strokeWidthKickFrames: 15,
    strokeWidthKickGradual: false,
    strokeWidthBlendFrames: 10,
    trailTaperStartLength: 0,
    trailTaperEndLength: 0,
    smoothPaths: false,
    mergeTrailToSinglePath: true,
    filledVariableWidthStroke: false,
    unionGroupOnComplete: true,
    backgroundColor: "#f4f2ee",
    exportBackground: false,
    groupSize: 1,
    groupRadius: 48,
    strokeLayoutCount: 1,
    strokeLayoutSpacing: 12,
    groupCohesionStrength: 6,
    kickIntervalSec: 1,
    kickIntervalRandomnessSec: 0,
    spawnChildIntervalSec: 0,
    spawnChildIntervalRandomnessSec: 0,
    childTrailCapMode: "none",
    childTrailCapFixed: 2400,
    childTrailCapRandomness: 0,
    stopAtMargin: true,
    forceMagnitude: 80,
    velocityKickGradual: false,
    velocityKickBlendFrames: 10,
    inputMode: DEFAULT_AGENT_INPUT_MODE,
};

/** Higher = velocity locks onto the field faster when flow influence is 1. */
const FLOW_STEER_RATE = 22;

/** Spring strength toward puppet brush target (leaders only), per second per unit distance. */
const PUPPET_PULL_K = 14;

export type SpawnAgentAtOptions = {
    anchorLeaderAtPointer?: boolean;
};

function speedScaleAt(
    grid: FlowGrid,
    x: number,
    y: number,
    stepVariation: number,
): number {
    if (stepVariation <= 0 || !grid.stepWeights) return 1;
    const w = sampleStepWeight(grid, x, y);
    return 1 + (w - 0.5) * 2 * stepVariation * 0.92;
}

function clampVelocityMag(
    vx: number,
    vy: number,
    maxSpeed: number,
): { vx: number; vy: number } {
    const mag = Math.hypot(vx, vy);
    if (mag <= maxSpeed || mag < 1e-9) return { vx, vy };
    const s = maxSpeed / mag;
    return { vx: vx * s, vy: vy * s };
}

function isInsideArtboard(
    x: number,
    y: number,
    width: number,
    height: number,
): boolean {
    return x >= 0 && x <= width && y >= 0 && y <= height;
}

function randF(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function randI(min: number, max: number): number {
    return Math.floor(randF(min, max + 0.999));
}

function ri(min: number, max: number): number {
    return Math.floor(min + Math.random() * (max - min + 1));
}

/** When a group leader completes, promote one follower to lead the rest. */
function repairFlowGroupsAfterLeaderLoss(
    agents: FlowFollowAgent[],
): FlowFollowAgent[] {
    const idSet = new Set(agents.map((a) => a.id));
    const byDeadLeader = new Map<string, FlowFollowAgent[]>();
    for (const a of agents) {
        if (a.leaderId && !idSet.has(a.leaderId)) {
            const list = byDeadLeader.get(a.leaderId) ?? [];
            list.push(a);
            byDeadLeader.set(a.leaderId, list);
        }
    }
    if (byDeadLeader.size === 0) return agents;

    const patch = new Map<string, Partial<FlowFollowAgent>>();
    for (const [, members] of byDeadLeader) {
        if (members.length === 0) continue;
        const nl = members[0]!;
        patch.set(nl.id, { leaderId: undefined, isGroupLeader: true });
        for (let i = 1; i < members.length; i++) {
            patch.set(members[i]!.id, {
                leaderId: nl.id,
                isGroupLeader: false,
            });
        }
    }
    return agents.map((a) => ({ ...a, ...(patch.get(a.id) ?? {}) }));
}

function randomHexByte(lo: number, hi: number): string {
    return Math.floor(randF(lo, hi))
        .toString(16)
        .padStart(2, "0");
}

export function FlowAgentsProvider({ children }: { children: React.ReactNode }) {
    const [width, setWidth] = useState(800);
    const [height, setHeight] = useState(600);
    const [marginFactor, setMarginFactor] = useState(0.5);
    const [cellSize, setCellSize] = useState(10);
    const [seed, setSeed] = useState("flow-agents-1");
    const [angleNoiseMode, setAngleNoiseMode] =
        useState<FlowAngleNoiseMode>("perlin");
    const [angleNoiseOctaves, setAngleNoiseOctaves] = useState(4);
    const [angleWarpStrength, setAngleWarpStrength] = useState(0.35);
    const [angleWarpFrequency, setAngleWarpFrequency] = useState(1);
    const [noiseScale, setNoiseScale] = useState(0.08);
    const [stepNoiseScale, setStepNoiseScale] = useState(0.09);
    const [stepVariation, setStepVariation] = useState(0);
    const [angleQuantizeDeg, setAngleQuantizeDeg] = useState(0);
    const [fieldRotationDeg, setFieldRotationDeg] = useState(0);
    const [mirrorNoiseX, setMirrorNoiseX] = useState(false);
    const [mirrorNoiseY, setMirrorNoiseY] = useState(false);
    const [uniformFlowBlend, setUniformFlowBlend] = useState(0);
    const [uniformFlowAngleDeg, setUniformFlowAngleDeg] = useState(0);
    const [showFlowField, setShowFlowField] = useState(true);
    const [followSpeed, setFollowSpeed] = useState(180);
    const [flowInfluence, setFlowInfluence] = useState(1);
    const [maxTrailLength, setMaxTrailLength] = useState(2400);
    const [maxTrailLengthRandomness, setMaxTrailLengthRandomness] = useState(0);
    const [strokeColor, setStrokeColor] = useState("#2a3d4a");
    const [strokePaletteText, setStrokePaletteText] = useState("");
    const [randomStrokeColorPerAgent, setRandomStrokeColorPerAgent] =
        useState(false);
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [minStrokeWidth, setMinStrokeWidth] = useState(0.25);
    const [initialStrokeWidth, setInitialStrokeWidth] = useState(0.45);
    const [strokeWidthStep, setStrokeWidthStep] = useState(0.5);
    const [strokeWidthKickFrames, setStrokeWidthKickFrames] = useState(15);
    const [strokeWidthKickGradual, setStrokeWidthKickGradual] = useState(false);
    const [strokeWidthBlendFrames, setStrokeWidthBlendFrames] = useState(10);
    const [trailTaperStartLength, setTrailTaperStartLength] = useState(0);
    const [trailTaperEndLength, setTrailTaperEndLength] = useState(0);
    const [smoothPaths, setSmoothPaths] = useState(false);
    const [mergeTrailToSinglePath, setMergeTrailToSinglePath] = useState(true);
    const [filledVariableWidthStroke, setFilledVariableWidthStroke] =
        useState(false);
    const [unionGroupOnComplete, setUnionGroupOnComplete] = useState(true);
    const [backgroundColor, setBackgroundColor] = useState("#f4f2ee");
    const [exportBackground, setExportBackground] = useState(false);
    const [stopAtMargin, setStopAtMargin] = useState(true);
    const [groupSize, setGroupSize] = useState(1);
    const [groupRadius, setGroupRadius] = useState(48);
    const [strokeLayoutCount, setStrokeLayoutCount] = useState(1);
    const [strokeLayoutSpacing, setStrokeLayoutSpacing] = useState(12);
    const [groupCohesionStrength, setGroupCohesionStrength] = useState(6);
    const [kickIntervalSec, setKickIntervalSec] = useState(1);
    const [kickIntervalRandomnessSec, setKickIntervalRandomnessSec] =
        useState(0);
    const [velocityKickGradual, setVelocityKickGradual] = useState(false);
    const [velocityKickBlendFrames, setVelocityKickBlendFrames] = useState(10);
    const [forceMagnitude, setForceMagnitude] = useState(80);
    const [spawnChildIntervalSec, setSpawnChildIntervalSec] = useState(0);
    const [spawnChildIntervalRandomnessSec, setSpawnChildIntervalRandomnessSec] =
        useState(0);
    const [childTrailCapMode, setChildTrailCapMode] =
        useState<ChildTrailCapMode>("none");
    const [childTrailCapFixed, setChildTrailCapFixed] = useState(2400);
    const [childTrailCapRandomness, setChildTrailCapRandomness] = useState(0);
    const [inputMode, setInputMode] =
        useState<AgentInputMode>(DEFAULT_AGENT_INPUT_MODE);

    const puppetBrushRef = useRef<{
        spawnGroupId: string;
        x: number;
        y: number;
    } | null>(null);

    const [completedPaths, setCompletedPaths] = useState<CompletedFlowAgentPath[]>(
        [],
    );
    const completedPathsRef = useRef<CompletedFlowAgentPath[]>([]);
    completedPathsRef.current = completedPaths;
    const [completedUnionGroups, setCompletedUnionGroups] = useState<
        CompletedFlowUnionGroup[]
    >([]);
    const agentsRef = useRef<FlowFollowAgent[]>([]);
    /** Seconds until next velocity kick; reset when no agents. */
    const timeToNextKickRef = useRef<number | null>(null);
    const frameIdxRef = useRef(0);
    const [tick, setTick] = useState(0);
    const bump = useCallback(() => setTick((t) => t + 1), []);

    const setPuppetBrushTarget = useCallback(
        (spawnGroupId: string, x: number, y: number) => {
            puppetBrushRef.current = { spawnGroupId, x, y };
        },
        [],
    );

    const clearPuppetBrush = useCallback(() => {
        puppetBrushRef.current = null;
    }, []);

    useEffect(() => {
        setStrokeWidth((w) => Math.max(w, minStrokeWidth));
    }, [minStrokeWidth]);

    useEffect(() => {
        setInitialStrokeWidth((w) =>
            Math.min(strokeWidth, Math.max(minStrokeWidth, w)),
        );
    }, [strokeWidth, minStrokeWidth]);

    const viewBoxAttr = `0 0 ${width} ${height}`;

    const quantizeRad = useMemo(
        () => (angleQuantizeDeg > 0 ? (angleQuantizeDeg * Math.PI) / 180 : 0),
        [angleQuantizeDeg],
    );

    const flowFieldMode = useMemo(
        (): FlowFieldModeOptions => ({
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
            flowShapeMode: "full",
            flowShapeCircleRadiusScale: 1,
        }),
        [
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
        ],
    );

    const angleNoiseParams = useMemo(
        () => ({
            mode: angleNoiseMode,
            octaves: angleNoiseOctaves,
            warpStrength: angleWarpStrength,
            warpFrequency: angleWarpFrequency,
        }),
        [
            angleNoiseMode,
            angleNoiseOctaves,
            angleWarpStrength,
            angleWarpFrequency,
        ],
    );

    const { flowGrid, debugSegments } = useMemo(() => {
        const noiseAngle = createFlowAngleNoise(seed || "default", angleNoiseParams);
        const noiseStep =
            stepVariation > 0
                ? createFlowAngleNoise(`${seed || "default"}|step`, {
                      mode: "perlin",
                      octaves: 1,
                      warpStrength: 0,
                      warpFrequency: 1,
                  })
                : null;
        const grid = buildFlowGrid(
            width,
            height,
            marginFactor,
            cellSize,
            noiseScale,
            noiseAngle,
            noiseStep,
            stepNoiseScale,
            flowFieldMode,
        );
        const minSide = Math.min(width, height);
        const debugVectorLen = Math.max(14, minSide / 28);
        const debug = showFlowField
            ? computeFlowFieldDebugSegments(
                  width,
                  height,
                  marginFactor,
                  cellSize,
                  noiseScale,
                  noiseAngle,
                  noiseStep,
                  stepNoiseScale,
                  quantizeRad,
                  {
                      vectorLen: debugVectorLen,
                      mode: flowFieldMode,
                      grid,
                  },
              )
            : [];
        return { flowGrid: grid, debugSegments: debug };
    }, [
        width,
        height,
        marginFactor,
        cellSize,
        seed,
        angleNoiseParams,
        noiseScale,
        stepNoiseScale,
        stepVariation,
        quantizeRad,
        showFlowField,
        flowFieldMode,
    ]);

    const gridRef = useRef(flowGrid);
    gridRef.current = flowGrid;

    const paramsRef = useRef({
        followSpeed,
        flowInfluence,
        maxTrailLength,
        maxTrailLengthRandomness,
        stepVariation,
        quantizeRad,
        width,
        height,
        strokeWidth,
        minStrokeWidth,
        initialStrokeWidth,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        unionGroupOnComplete,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        groupCohesionStrength,
        kickIntervalSec,
        kickIntervalRandomnessSec,
        velocityKickGradual,
        velocityKickBlendFrames,
        forceMagnitude,
        trailTaperStartLength,
        trailTaperEndLength,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        strokeColor,
        strokePaletteText,
        randomStrokeColorPerAgent,
        stopAtMargin,
        strokeLayoutCount,
        strokeLayoutSpacing,
    });
    paramsRef.current = {
        followSpeed,
        flowInfluence,
        maxTrailLength,
        maxTrailLengthRandomness,
        stepVariation,
        quantizeRad,
        width,
        height,
        strokeWidth,
        minStrokeWidth,
        initialStrokeWidth,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        unionGroupOnComplete,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        groupCohesionStrength,
        kickIntervalSec,
        kickIntervalRandomnessSec,
        velocityKickGradual,
        velocityKickBlendFrames,
        forceMagnitude,
        trailTaperStartLength,
        trailTaperEndLength,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        strokeColor,
        strokePaletteText,
        randomStrokeColorPerAgent,
        stopAtMargin,
        strokeLayoutCount,
        strokeLayoutSpacing,
    };

    useEffect(() => {
        let raf = 0;
        let last = performance.now();

        const loop = (now: number) => {
            const dt = Math.min(0.1, (now - last) / 1000);
            last = now;

            frameIdxRef.current += 1;

            const grid = gridRef.current;
            const {
                followSpeed: speed,
                flowInfluence: influence,
                stepVariation: stepVar,
                quantizeRad: qRad,
                width: w,
                height: h,
                strokeWidth: maxStrokeW,
                minStrokeWidth: minStrokeW,
                mergeTrailToSinglePath: mergeVaryingWidth,
                filledVariableWidthStroke: filledVariableWidth,
                unionGroupOnComplete: shouldUnionOnGroupComplete,
                strokeWidthStep: swStep,
                strokeWidthKickFrames: swKickFrames,
                strokeWidthKickGradual: gradualSwKick,
                strokeWidthBlendFrames: swBlendN,
                groupCohesionStrength: cohesionK,
                kickIntervalSec: kInt,
                kickIntervalRandomnessSec: kJitter,
                velocityKickGradual: gradualKick,
                velocityKickBlendFrames: kickBlendN,
                forceMagnitude: fMag,
                trailTaperStartLength: taperStartLen,
                trailTaperEndLength: taperEndLen,
                spawnChildIntervalSec: sci,
                spawnChildIntervalRandomnessSec: scr,
                maxTrailLength: maxTrailBase,
                maxTrailLengthRandomness: maxTrailRand,
                childTrailCapMode: childTrailCapModeParam,
                childTrailCapFixed: childTrailCapFixedParam,
                childTrailCapRandomness: childTrailCapRandomnessParam,
                stopAtMargin: stopAtEdge,
                strokeLayoutCount: strokeLayoutCountParam,
                strokeLayoutSpacing: strokeLayoutSpacingParam,
            } = paramsRef.current;

            const strokeLayoutK = Math.max(
                1,
                Math.min(32, Math.round(strokeLayoutCountParam)),
            );
            const strokeLayoutSpacingPx = Math.max(0, strokeLayoutSpacingParam);

            const maxSpeed = Math.max(speed * 2, 1);

            if (agentsRef.current.length === 0) {
                timeToNextKickRef.current = null;
            } else {
                if (timeToNextKickRef.current === null) {
                    timeToNextKickRef.current = sampleKickDelay(kInt, kJitter);
                }
                timeToNextKickRef.current -= dt;
                while (
                    timeToNextKickRef.current <= 0 &&
                    agentsRef.current.length > 0
                ) {
                    agentsRef.current = agentsRef.current.map((a) => {
                        if (a.parallelStrokeLeaderId) return a;
                        const k = applyVelocityKick(
                            a.vx,
                            a.vy,
                            fMag,
                            maxSpeed,
                        );
                        if (gradualKick) {
                            const N = Math.max(1, Math.round(kickBlendN));
                            return {
                                ...a,
                                kickPerFrameVx: (k.vx - a.vx) / N,
                                kickPerFrameVy: (k.vy - a.vy) / N,
                                kickBlendRemaining: N,
                            };
                        }
                        return {
                            ...a,
                            vx: k.vx,
                            vy: k.vy,
                            kickPerFrameVx: undefined,
                            kickPerFrameVy: undefined,
                            kickBlendRemaining: undefined,
                        };
                    });
                    timeToNextKickRef.current += sampleKickDelay(
                        kInt,
                        kJitter,
                    );
                }
            }

            const byId = new Map(
                agentsRef.current.map((a) => [a.id, a] as const),
            );

            if (
                agentsRef.current.length > 0 &&
                swKickFrames > 0 &&
                frameIdxRef.current % swKickFrames === 0
            ) {
                agentsRef.current = agentsRef.current.map((a) => {
                    if (a.parallelStrokeLeaderId) return a;
                    const target = applyStrokeWidthKick(
                        a.liveStrokeWidth,
                        swStep,
                        minStrokeW,
                        maxStrokeW,
                    );
                    if (gradualSwKick) {
                        const N = Math.max(1, Math.round(swBlendN));
                        return {
                            ...a,
                            strokeWidthPerFrameW:
                                (target - a.liveStrokeWidth) / N,
                            strokeWidthBlendRemaining: N,
                        };
                    }
                    return {
                        ...a,
                        liveStrokeWidth: target,
                        strokeWidthPerFrameW: undefined,
                        strokeWidthBlendRemaining: undefined,
                    };
                });
            }

            const nextAgents: FlowFollowAgent[] = [];
            const finished: CompletedFlowAgentPath[] = [];
            const childSpawns: FlowFollowAgent[] = [];

            const finishParallelSlavesForPrimary = (primary: FlowFollowAgent) => {
                if (!primary.parallelStrokeSlaveIds?.length) return;
                for (const sid of primary.parallelStrokeSlaveIds) {
                    const s = agentsRef.current.find((a) => a.id === sid);
                    if (s && s.points.length >= 2) {
                        finished.push({
                            id: s.id,
                            spawnGroupId: s.spawnGroupId,
                            points: s.points,
                            strokeWidths:
                                s.strokeWidths.length === s.points.length
                                    ? s.strokeWidths
                                    : undefined,
                            strokeColor: s.strokeColor,
                        });
                    }
                }
            };

            const newFlowAgentId = () =>
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `a-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

            const makeFlowChildAgents = (
                spawnGroupId: string,
                nx: number,
                ny: number,
                cvx: number,
                cvy: number,
                liveW: number,
                trailCap: number,
            ): FlowFollowAgent[] => {
                const pickFlowStroke = () =>
                    resolveAgentStrokeColor({
                        randomPerAgent:
                            paramsRef.current.randomStrokeColorPerAgent,
                        paletteText: paramsRef.current.strokePaletteText,
                        primaryStrokeColor: paramsRef.current.strokeColor,
                    });
                const childLayoutK = Math.max(
                    1,
                    Math.min(32, Math.round(strokeLayoutCountParam)),
                );
                const childLayoutSpacing = Math.max(0, strokeLayoutSpacingParam);
                if (childLayoutK === 1) {
                    return [
                        {
                            id: newFlowAgentId(),
                            spawnGroupId,
                            x: nx,
                            y: ny,
                            vx: cvx,
                            vy: cvy,
                            points: [{ x: nx, y: ny }],
                            strokeWidths: [liveW],
                            liveStrokeWidth: liveW,
                            strokeColor: pickFlowStroke(),
                            arcLength: 0,
                            trailLengthCap: trailCap,
                            childSpawnTimerSec:
                                sci > 0 ? sampleKickDelay(sci, scr) : undefined,
                        },
                    ];
                }
                const { nx: perpNx, ny: perpNy } = unitPerpendicularToMotion(
                    null,
                    cvx,
                    cvy,
                );
                const layoutMid = Math.floor((childLayoutK - 1) / 2);
                const slotIds: string[] = [];
                for (let j = 0; j < childLayoutK; j++) {
                    slotIds.push(newFlowAgentId());
                }
                const primaryId = slotIds[layoutMid]!;
                const slaveIds = slotIds.filter((_, idx) => idx !== layoutMid);
                const fb: ViewBoxBounds = {
                    minX: 0,
                    minY: 0,
                    maxX: w,
                    maxY: h,
                };
                const out: FlowFollowAgent[] = [];
                for (let j = 0; j < childLayoutK; j++) {
                    const t = j - (childLayoutK - 1) / 2;
                    const ax = nx + perpNx * childLayoutSpacing * t;
                    const ay = ny + perpNy * childLayoutSpacing * t;
                    const c = clampPointInsideBounds(ax, ay, fb);
                    if (stopAtEdge && isOutsideOrOnEdge(c.x, c.y, fb)) {
                        if (j === layoutMid) {
                            return [];
                        }
                        continue;
                    }
                    const id = slotIds[j]!;
                    const isPrimary = j === layoutMid;
                    let parallelStrokeLeaderId: string | undefined;
                    let parallelStrokeSlotIndex: number | undefined;
                    let parallelStrokeSlaveIds: string[] | undefined;
                    if (isPrimary) {
                        parallelStrokeSlaveIds = slaveIds;
                    } else {
                        parallelStrokeLeaderId = primaryId;
                        parallelStrokeSlotIndex = j;
                    }
                    out.push({
                        id,
                        spawnGroupId,
                        x: c.x,
                        y: c.y,
                        vx: cvx,
                        vy: cvy,
                        points: [{ x: c.x, y: c.y }],
                        strokeWidths: [liveW],
                        liveStrokeWidth: liveW,
                        strokeColor: pickFlowStroke(),
                        arcLength: 0,
                        trailLengthCap: trailCap,
                        parallelStrokeLeaderId,
                        parallelStrokeSlotIndex,
                        parallelStrokeSlaveIds,
                        childSpawnTimerSec:
                            sci > 0 ? sampleKickDelay(sci, scr) : undefined,
                    });
                }
                return out;
            };

            for (const agent of agentsRef.current) {
                if (agent.parallelStrokeLeaderId) {
                    continue;
                }
                const maxLen = agent.trailLengthCap;
                let vx = agent.vx;
                let vy = agent.vy;
                const kRem = agent.kickBlendRemaining ?? 0;
                if (
                    kRem > 0 &&
                    agent.kickPerFrameVx !== undefined &&
                    agent.kickPerFrameVy !== undefined
                ) {
                    vx += agent.kickPerFrameVx;
                    vy += agent.kickPerFrameVy;
                }

                let liveW = agent.liveStrokeWidth;
                const swRem = agent.strokeWidthBlendRemaining ?? 0;
                if (
                    swRem > 0 &&
                    agent.strokeWidthPerFrameW !== undefined
                ) {
                    liveW = Math.min(
                        maxStrokeW,
                        Math.max(
                            minStrokeW,
                            agent.liveStrokeWidth + agent.strokeWidthPerFrameW,
                        ),
                    );
                }

                let ang = sampleAngle(grid, agent.x, agent.y);
                if (qRad > 0) ang = quantizeAngleRad(ang, qRad);
                const scale = speedScaleAt(grid, agent.x, agent.y, stepVar);
                const desiredSpeed = speed * scale;
                const desiredVx = Math.cos(ang) * desiredSpeed;
                const desiredVy = Math.sin(ang) * desiredSpeed;

                const steer = influence * FLOW_STEER_RATE;
                const kSteer = 1 - Math.exp(-steer * dt);
                vx = vx + (desiredVx - vx) * kSteer;
                vy = vy + (desiredVy - vy) * kSteer;
                let clamped = clampVelocityMag(vx, vy, maxSpeed);
                vx = clamped.vx;
                vy = clamped.vy;

                if (agent.leaderId) {
                    const L = byId.get(agent.leaderId);
                    if (L) {
                        vx += cohesionK * (L.x - agent.x) * dt;
                        vy += cohesionK * (L.y - agent.y) * dt;
                        clamped = clampVelocityMag(vx, vy, maxSpeed);
                        vx = clamped.vx;
                        vy = clamped.vy;
                    }
                }

                const pup = puppetBrushRef.current;
                if (
                    pup &&
                    agent.leaderId == null &&
                    agent.spawnGroupId === pup.spawnGroupId
                ) {
                    vx += PUPPET_PULL_K * (pup.x - agent.x) * dt;
                    vy += PUPPET_PULL_K * (pup.y - agent.y) * dt;
                    clamped = clampVelocityMag(vx, vy, maxSpeed);
                    vx = clamped.vx;
                    vy = clamped.vy;
                }

                const remaining = maxLen - agent.arcLength;
                if (remaining <= 0) {
                    if (agent.points.length >= 2) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            points: agent.points,
                            strokeWidths:
                                agent.strokeWidths.length === agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                        finishParallelSlavesForPrimary(agent);
                    }
                    continue;
                }

                let moveX = vx * dt;
                let moveY = vy * dt;
                const moveDist = Math.hypot(moveX, moveY);
                if (moveDist <= 1e-12) {
                    if (agent.points.length >= 2) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            points: agent.points,
                            strokeWidths:
                                agent.strokeWidths.length === agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                        finishParallelSlavesForPrimary(agent);
                    }
                    continue;
                }

                let stepDist = moveDist;
                if (stepDist > remaining) {
                    const s = remaining / stepDist;
                    moveX *= s;
                    moveY *= s;
                    stepDist = remaining;
                }

                const nx = agent.x + moveX;
                const ny = agent.y + moveY;

                if (stopAtEdge && !isInsideArtboard(nx, ny, w, h)) {
                    if (agent.points.length >= 2) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            points: agent.points,
                            strokeWidths:
                                agent.strokeWidths.length === agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                        finishParallelSlavesForPrimary(agent);
                    }
                    continue;
                }

                const newArc = agent.arcLength + stepDist;
                const newPoints = [...agent.points, { x: nx, y: ny }];
                const newStrokeWidths = [...agent.strokeWidths, liveW];

                if (newArc >= maxLen - 1e-6) {
                    finished.push({
                        id: agent.id,
                        spawnGroupId: agent.spawnGroupId,
                        points: newPoints,
                        strokeWidths:
                            newStrokeWidths.length === newPoints.length
                                ? newStrokeWidths
                                : undefined,
                        strokeColor: agent.strokeColor,
                    });
                    finishParallelSlavesForPrimary(agent);
                    continue;
                }

                const nextKickRem = kRem > 0 ? kRem - 1 : 0;
                const nextKickPx =
                    nextKickRem > 0 ? agent.kickPerFrameVx : undefined;
                const nextKickPy =
                    nextKickRem > 0 ? agent.kickPerFrameVy : undefined;

                const nextSwRem = swRem > 0 ? swRem - 1 : 0;
                const nextSwPw =
                    nextSwRem > 0 ? agent.strokeWidthPerFrameW : undefined;

                const { nextSeconds: nextChildT, shouldSpawn } =
                    advancePeriodicSpawnTimer(
                        agent.childSpawnTimerSec,
                        dt,
                        sci,
                        scr,
                    );

                nextAgents.push({
                    ...agent,
                    x: nx,
                    y: ny,
                    vx,
                    vy,
                    liveStrokeWidth: liveW,
                    points: newPoints,
                    strokeWidths: newStrokeWidths,
                    arcLength: newArc,
                    childSpawnTimerSec: nextChildT,
                    kickBlendRemaining: nextKickRem,
                    kickPerFrameVx: nextKickPx,
                    kickPerFrameVy: nextKickPy,
                    strokeWidthBlendRemaining: nextSwRem,
                    strokeWidthPerFrameW: nextSwPw,
                });

                if (shouldSpawn) {
                    let angC = sampleAngle(grid, nx, ny);
                    if (qRad > 0) angC = quantizeAngleRad(angC, qRad);
                    const scaleC = speedScaleAt(grid, nx, ny, stepVar);
                    const spdC = speed * scaleC;
                    let cvx = Math.cos(angC) * spdC;
                    let cvy = Math.sin(angC) * spdC;
                    const cclamped = clampVelocityMag(cvx, cvy, maxSpeed);
                    cvx = cclamped.vx;
                    cvy = cclamped.vy;
                    const trailCapChild = computeChildTrailLengthCap({
                        mode: childTrailCapModeParam,
                        maxTrailLength: maxTrailBase,
                        maxTrailLengthRandomness: maxTrailRand,
                        parentRemaining: agent.trailLengthCap - newArc,
                        fixedCap: childTrailCapFixedParam,
                        childTrailCapRandomness: childTrailCapRandomnessParam,
                    });
                    childSpawns.push(
                        ...makeFlowChildAgents(
                            agent.spawnGroupId,
                            nx,
                            ny,
                            cvx,
                            cvy,
                            liveW,
                            trailCapChild,
                        ),
                    );
                }
            }

            const nextByIdFlow = new Map(nextAgents.map((a) => [a.id, a]));

            for (const agent of agentsRef.current) {
                if (!agent.parallelStrokeLeaderId) {
                    continue;
                }
                const leader = nextByIdFlow.get(agent.parallelStrokeLeaderId);
                if (!leader) {
                    continue;
                }
                const slotT =
                    (agent.parallelStrokeSlotIndex ?? 0) -
                    (strokeLayoutK - 1) / 2;
                const { nx: pnx, ny: pny } = unitPerpendicularToMotion(
                    null,
                    leader.vx,
                    leader.vy,
                );
                const px = leader.x + pnx * strokeLayoutSpacingPx * slotT;
                const py = leader.y + pny * strokeLayoutSpacingPx * slotT;

                if (stopAtEdge && !isInsideArtboard(px, py, w, h)) {
                    if (agent.points.length >= 2) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            points: agent.points,
                            strokeWidths:
                                agent.strokeWidths.length === agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                    }
                    continue;
                }

                const newArcSlave = leader.arcLength;
                if (newArcSlave >= agent.trailLengthCap - 1e-6) {
                    finished.push({
                        id: agent.id,
                        spawnGroupId: agent.spawnGroupId,
                        points: [...agent.points, { x: px, y: py }],
                        strokeWidths:
                            agent.strokeWidths.length === agent.points.length
                                ? [...agent.strokeWidths, leader.liveStrokeWidth]
                                : undefined,
                        strokeColor: agent.strokeColor,
                    });
                    continue;
                }

                nextAgents.push({
                    ...agent,
                    x: px,
                    y: py,
                    vx: leader.vx,
                    vy: leader.vy,
                    liveStrokeWidth: leader.liveStrokeWidth,
                    points: [...agent.points, { x: px, y: py }],
                    strokeWidths: [...agent.strokeWidths, leader.liveStrokeWidth],
                    arcLength: newArcSlave,
                    kickBlendRemaining: leader.kickBlendRemaining,
                    kickPerFrameVx: leader.kickPerFrameVx,
                    kickPerFrameVy: leader.kickPerFrameVy,
                    strokeWidthBlendRemaining: leader.strokeWidthBlendRemaining,
                    strokeWidthPerFrameW: leader.strokeWidthPerFrameW,
                    childSpawnTimerSec: leader.childSpawnTimerSec,
                });
            }

            nextAgents.push(...childSpawns);

            if (finished.length > 0) {
                const mergedCompleted = [...completedPathsRef.current, ...finished];
                setCompletedPaths(mergedCompleted);

                if (shouldUnionOnGroupComplete) {
                    const candidateGroupIds = new Set(
                        finished.map((p) => p.spawnGroupId),
                    );
                    const stillActiveGroupIds = new Set(
                        nextAgents.map((a) => a.spawnGroupId),
                    );
                    const finalizedGroupIds = [...candidateGroupIds].filter(
                        (gid) => !stillActiveGroupIds.has(gid),
                    );

                    if (finalizedGroupIds.length > 0) {
                        setCompletedUnionGroups((prev) => {
                            const byGroup = new Map(
                                prev.map((g) => [g.spawnGroupId, g] as const),
                            );
                            for (const spawnGroupId of finalizedGroupIds) {
                                const groupTrails = mergedCompleted.filter(
                                    (p) =>
                                        p.spawnGroupId === spawnGroupId &&
                                        p.points.length >= 2,
                                );
                                const unioned = buildFlowAgentsGroupUnion({
                                    trails: groupTrails,
                                    fallbackStrokeWidth: maxStrokeW,
                                    mergeTrailToSinglePath: mergeVaryingWidth,
                                    filledVariableWidthStroke: filledVariableWidth,
                                    trailTaperStartLength: taperStartLen,
                                    trailTaperEndLength: taperEndLen,
                                });
                                if (!unioned) continue;
                                byGroup.set(spawnGroupId, {
                                    spawnGroupId,
                                    pathD: unioned.pathD,
                                    fill: unioned.fill,
                                });
                            }
                            return [...byGroup.values()];
                        });
                    }
                }
            }

            agentsRef.current = repairFlowGroupsAfterLeaderLoss(nextAgents);
            if (nextAgents.length > 0 || finished.length > 0) {
                bump();
            }

            raf = requestAnimationFrame(loop);
        };

        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [bump]);

    const spawnAgentAt = useCallback(
        (
            x: number,
            y: number,
            initialDir?: { dx: number; dy: number } | null,
            options?: SpawnAgentAtOptions,
        ): string | undefined => {
            if (!isInsideArtboard(x, y, width, height)) return undefined;

            const anchorLeaderAtPointer = options?.anchorLeaderAtPointer === true;

            const grid = gridRef.current;
            const {
                followSpeed: spawnSpeed,
                stepVariation: stepVar,
                quantizeRad: qRad,
                minStrokeWidth: minW,
                initialStrokeWidth: initW,
                strokeWidth: maxW,
                maxTrailLength: spawnTrailBase,
                maxTrailLengthRandomness: spawnTrailRand,
                spawnChildIntervalSec: sci,
                spawnChildIntervalRandomnessSec: scr,
            } = paramsRef.current;

            const w0 = Math.min(maxW, Math.max(minW, initW));
            const spawnGroupId =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

            const newId = () =>
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `a-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

            const velocityAt = (px: number, py: number) => {
                let ang = sampleAngle(grid, px, py);
                if (qRad > 0) ang = quantizeAngleRad(ang, qRad);
                const scale = speedScaleAt(grid, px, py, stepVar);
                const spd = spawnSpeed * scale;
                return {
                    vx: Math.cos(ang) * spd,
                    vy: Math.sin(ang) * spd,
                };
            };

            const centerField = velocityAt(x, y);
            const centerSpeed = Math.hypot(centerField.vx, centerField.vy);
            let dirUx = 0;
            let dirUy = 0;
            let useInitialDir = false;
            if (initialDir) {
                const dlen = Math.hypot(initialDir.dx, initialDir.dy);
                if (dlen >= 1e-9) {
                    dirUx = initialDir.dx / dlen;
                    dirUy = initialDir.dy / dlen;
                    useInitialDir = true;
                }
            }

            const initialVelocityForAgent = (px: number, py: number) => {
                if (!useInitialDir) return velocityAt(px, py);
                return {
                    vx: dirUx * centerSpeed,
                    vy: dirUy * centerSpeed,
                };
            };

            const bounds: ViewBoxBounds = {
                minX: 0,
                minY: 0,
                maxX: width,
                maxY: height,
            };

            const createAgent = (
                px: number,
                py: number,
                id: string,
                groupOpts: { leaderId?: string; isGroupLeader?: boolean },
                parallelOpts?: {
                    parallelStrokeLeaderId?: string;
                    parallelStrokeSlotIndex?: number;
                    parallelStrokeSlaveIds?: string[];
                },
            ): FlowFollowAgent => {
                const { vx, vy } = initialVelocityForAgent(px, py);
                return {
                    id,
                    spawnGroupId,
                    x: px,
                    y: py,
                    vx,
                    vy,
                    points: [{ x: px, y: py }],
                    strokeWidths: [w0],
                    liveStrokeWidth: w0,
                    strokeColor: resolveAgentStrokeColor({
                        randomPerAgent:
                            paramsRef.current.randomStrokeColorPerAgent,
                        paletteText: paramsRef.current.strokePaletteText,
                        primaryStrokeColor: paramsRef.current.strokeColor,
                    }),
                    arcLength: 0,
                    trailLengthCap: sampleTrailLengthCap(
                        spawnTrailBase,
                        spawnTrailRand,
                    ),
                    childSpawnTimerSec:
                        sci > 0 ? sampleKickDelay(sci, scr) : undefined,
                    ...groupOpts,
                    ...parallelOpts,
                };
            };

            const n = Math.max(1, Math.min(32, Math.round(groupSize)));
            const layoutK = Math.max(1, Math.min(32, Math.round(strokeLayoutCount)));
            const layoutSpacing = Math.max(0, strokeLayoutSpacing);
            const { nx: perpNx, ny: perpNy } = unitPerpendicularToMotion(
                initialDir,
                centerField.vx,
                centerField.vy,
            );
            const layoutMid = Math.floor((layoutK - 1) / 2);
            const offsetForLayout = (j: number) => {
                const t = j - (layoutK - 1) / 2;
                return {
                    dx: perpNx * layoutSpacing * t,
                    dy: perpNy * layoutSpacing * t,
                };
            };

            type SlotRole =
                | { kind: "solo" }
                | { kind: "groupLeader" }
                | { kind: "groupFollower"; mainLeaderId: string };

            const pushSlotAgents = (
                out: FlowFollowAgent[],
                px: number,
                py: number,
                role: SlotRole,
            ): string | undefined => {
                if (layoutK === 1) {
                    const id = newId();
                    const c = clampPointInsideBounds(px, py, bounds);
                    let leaderId: string | undefined;
                    let isGroupLeader: boolean | undefined;
                    if (role.kind === "solo") {
                        leaderId = undefined;
                        isGroupLeader = undefined;
                    } else if (role.kind === "groupLeader") {
                        leaderId = undefined;
                        isGroupLeader = true;
                    } else {
                        leaderId = role.mainLeaderId;
                        isGroupLeader = false;
                    }
                    out.push(createAgent(c.x, c.y, id, { leaderId, isGroupLeader }));
                    return role.kind === "groupLeader" ? id : undefined;
                }

                const slotIds: string[] = [];
                for (let j = 0; j < layoutK; j++) {
                    slotIds.push(newId());
                }
                const primaryId = slotIds[layoutMid]!;
                const slaveIds = slotIds.filter((_, idx) => idx !== layoutMid);

                for (let j = 0; j < layoutK; j++) {
                    const off = offsetForLayout(j);
                    const c = clampPointInsideBounds(
                        px + off.dx,
                        py + off.dy,
                        bounds,
                    );
                    const id = slotIds[j]!;
                    const isPrimary = j === layoutMid;

                    let leaderId: string | undefined;
                    let isGroupLeader: boolean | undefined;
                    let parallelStrokeLeaderId: string | undefined;
                    let parallelStrokeSlotIndex: number | undefined;
                    let parallelStrokeSlaveIds: string[] | undefined;

                    if (isPrimary) {
                        parallelStrokeSlaveIds = slaveIds;
                        if (role.kind === "solo") {
                            leaderId = undefined;
                            isGroupLeader = undefined;
                        } else if (role.kind === "groupLeader") {
                            leaderId = undefined;
                            isGroupLeader = true;
                        } else {
                            leaderId = role.mainLeaderId;
                            isGroupLeader = false;
                        }
                    } else {
                        parallelStrokeLeaderId = primaryId;
                        parallelStrokeSlotIndex = j;
                    }

                    out.push(
                        createAgent(c.x, c.y, id, { leaderId, isGroupLeader }, {
                            parallelStrokeLeaderId,
                            parallelStrokeSlotIndex,
                            parallelStrokeSlaveIds,
                        }),
                    );
                }
                return role.kind === "groupLeader" ? primaryId : undefined;
            };

            if (n === 1) {
                const newOnes: FlowFollowAgent[] = [];
                pushSlotAgents(newOnes, x, y, { kind: "solo" });
                agentsRef.current = [...agentsRef.current, ...newOnes];
                bump();
                return spawnGroupId;
            }

            const newOnes: FlowFollowAgent[] = [];

            if (anchorLeaderAtPointer) {
                const followerPos: { x: number; y: number }[] = [];
                let guard = 0;
                while (followerPos.length < n - 1 && guard++ < 400) {
                    let p = randomPointInDisk(x, y, groupRadius);
                    p = clampPointInsideBounds(p.x, p.y, bounds);
                    if (!isOutsideOrOnEdge(p.x, p.y, bounds)) {
                        followerPos.push(p);
                    }
                }
                while (followerPos.length < n - 1) {
                    followerPos.push(clampPointInsideBounds(x, y, bounds));
                }

                const mainLeaderParallelId = pushSlotAgents(
                    newOnes,
                    x,
                    y,
                    { kind: "groupLeader" },
                )!;

                for (let i = 1; i < n; i++) {
                    const pos = followerPos[i - 1]!;
                    pushSlotAgents(newOnes, pos.x, pos.y, {
                        kind: "groupFollower",
                        mainLeaderId: mainLeaderParallelId,
                    });
                }
            } else {
                const positions: { x: number; y: number }[] = [];
                let guard = 0;
                while (positions.length < n && guard++ < 400) {
                    let p = randomPointInDisk(x, y, groupRadius);
                    p = clampPointInsideBounds(p.x, p.y, bounds);
                    if (!isOutsideOrOnEdge(p.x, p.y, bounds)) {
                        positions.push(p);
                    }
                }
                while (positions.length < n) {
                    positions.push(clampPointInsideBounds(x, y, bounds));
                }

                const leaderIdx = ri(0, n - 1);
                const slotOrder = [
                    leaderIdx,
                    ...Array.from({ length: n }, (_, i) => i).filter(
                        (i) => i !== leaderIdx,
                    ),
                ];

                let mainLeaderParallelId: string | undefined;
                for (const slotIdx of slotOrder) {
                    const pos = positions[slotIdx]!;
                    if (slotIdx === leaderIdx) {
                        mainLeaderParallelId = pushSlotAgents(
                            newOnes,
                            pos.x,
                            pos.y,
                            { kind: "groupLeader" },
                        );
                    } else {
                        pushSlotAgents(newOnes, pos.x, pos.y, {
                            kind: "groupFollower",
                            mainLeaderId: mainLeaderParallelId!,
                        });
                    }
                }
            }

            agentsRef.current = [...agentsRef.current, ...newOnes];
            bump();
            return spawnGroupId;
        },
        [
            width,
            height,
            strokeColor,
            strokePaletteText,
            randomStrokeColorPerAgent,
            bump,
            groupSize,
            groupRadius,
            strokeLayoutCount,
            strokeLayoutSpacing,
            maxTrailLength,
            maxTrailLengthRandomness,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
        ],
    );

    const clearTrails = useCallback(() => {
        puppetBrushRef.current = null;
        agentsRef.current = [];
        setCompletedPaths([]);
        setCompletedUnionGroups([]);
        bump();
    }, [bump]);

    const activeAgents = agentsRef.current;

    const canExport = useMemo(
        () =>
            completedPaths.some((p) => p.points.length >= 2) ||
            completedUnionGroups.some((g) => g.pathD.length > 0) ||
            activeAgents.some((a) => a.points.length >= 2),
        [completedPaths, completedUnionGroups, activeAgents, tick],
    );

    const buildExportSvgString = useCallback(() => {
        const fragmentsFor = (
            points: Point2[],
            sw: number[] | undefined,
            color: string,
        ) =>
            trailToSvgFragments(
                points,
                sw,
                smoothPaths,
                color,
                strokeWidth,
                mergeTrailToSinglePath,
                undefined,
                filledVariableWidthStroke,
                trailTaperStartLength,
                trailTaperEndLength,
            );

        const byGroup = new Map<string, string[]>();
        const unionedByGroup = new Map(
            completedUnionGroups.map((g) => [g.spawnGroupId, g] as const),
        );
        const pushFrags = (spawnGroupId: string, frags: string[]) => {
            if (frags.length === 0) return;
            const prev = byGroup.get(spawnGroupId) ?? [];
            byGroup.set(spawnGroupId, [...prev, ...frags]);
        };

        for (const c of completedPaths) {
            if (unionGroupOnComplete && unionedByGroup.has(c.spawnGroupId)) {
                continue;
            }
            if (c.points.length < 2) continue;
            pushFrags(
                c.spawnGroupId,
                fragmentsFor(c.points, c.strokeWidths, c.strokeColor),
            );
        }
        for (const a of agentsRef.current) {
            if (unionGroupOnComplete && unionedByGroup.has(a.spawnGroupId)) {
                continue;
            }
            if (a.points.length < 2) continue;
            pushFrags(
                a.spawnGroupId,
                fragmentsFor(a.points, a.strokeWidths, a.strokeColor),
            );
        }
        if (unionGroupOnComplete) {
            for (const g of completedUnionGroups) {
                if (!g.pathD) continue;
                pushFrags(g.spawnGroupId, [
                    `<path d="${g.pathD}" fill="${g.fill}" fill-rule="nonzero" stroke="none" />`,
                ]);
            }
        }

        const blocks: string[] = [];
        for (const [spawnGroupId, frags] of byGroup) {
            const inner = frags.map((line) => `    ${line}`).join("\n");
            blocks.push(
                `  <g class="flow-agents-spawn-group" data-spawn-group="${spawnGroupId}">\n${inner}\n  </g>`,
            );
        }

        const bgRect = exportBackground
            ? `  <rect width="${width}" height="${height}" fill="${backgroundColor}" />\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${bgRect}${blocks.join("\n")}
</svg>`;
    }, [
        completedPaths,
        completedUnionGroups,
        unionGroupOnComplete,
        strokeWidth,
        smoothPaths,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        trailTaperStartLength,
        trailTaperEndLength,
        backgroundColor,
        exportBackground,
        width,
        height,
        viewBoxAttr,
        tick,
    ]);

    const handleDownload = useCallback(() => {
        const svg = buildExportSvgString();
        const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `flow-agents-${seed.replace(/[^\w.-]+/g, "_")}-${Date.now()}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, seed]);

    const randomizeParameters = useCallback(() => {
        const w = Math.max(120, Math.round(randI(400, 1200) / 40) * 40);
        const h = Math.max(120, Math.round(randI(320, 960) / 40) * 40);
        setWidth(w);
        setHeight(h);
        setMarginFactor(Math.round(randF(0.15, 0.75) * 100) / 100);
        setCellSize(randI(5, 22));
        setSeed(
            `fa-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        );
        const modes: FlowAngleNoiseMode[] = [
            "perlin",
            "fbm",
            "domain_warp",
        ];
        setAngleNoiseMode(modes[randI(0, modes.length - 1)]!);
        setAngleNoiseOctaves(randI(2, 6));
        setAngleWarpStrength(Math.round(randF(0.15, 0.85) * 100) / 100);
        setAngleWarpFrequency(Math.round(randF(0.4, 2.2) * 100) / 100);
        setNoiseScale(Math.round(randF(0.04, 0.22) * 1000) / 1000);
        setStepNoiseScale(Math.round(randF(0.05, 0.24) * 1000) / 1000);
        setStepVariation(Math.round(randF(0, 1) * 100) / 100);
        const qOpts = [0, 0, 0, 5, 10, 15, 22.5, 30, 45];
        setAngleQuantizeDeg(qOpts[randI(0, qOpts.length - 1)] ?? 0);
        setFieldRotationDeg(Math.round(randF(-90, 90) * 2) / 2);
        setMirrorNoiseX(Math.random() < 0.2);
        setMirrorNoiseY(Math.random() < 0.2);
        setUniformFlowBlend(Math.round(randF(0, 0.45) * 100) / 100);
        setUniformFlowAngleDeg(Math.round(randF(0, 360) / 5) * 5);
        setFollowSpeed(Math.round(randF(80, 420) / 5) * 5);
        setFlowInfluence(Math.round(randF(0, 1) * 100) / 100);
        setMaxTrailLength(Math.round(randI(8, 80)) * 100);
        setMaxTrailLengthRandomness(Math.round(randF(0, 0.85) * 100) / 100);
        setGroupSize(randI(1, 12));
        setGroupRadius(Math.round(randF(8, 160) / 2) * 2);
        setStrokeLayoutCount(randI(1, 6));
        setStrokeLayoutSpacing(Math.round(randF(4, 36)));
        setGroupCohesionStrength(Math.round(randF(2, 14) * 10) / 10);
        setKickIntervalSec(Math.round(randF(0.5, 4) * 4) / 4);
        setKickIntervalRandomnessSec(Math.round(randF(0, 1.5) * 20) / 20);
        setSpawnChildIntervalSec(Math.random() < 0.55 ? 0 : Math.round(randF(0.25, 3.5) * 20) / 20);
        setSpawnChildIntervalRandomnessSec(Math.round(randF(0, 1.2) * 20) / 20);
        const capModes: ChildTrailCapMode[] = [
            "none",
            "parentRemaining",
            "fixed",
        ];
        setChildTrailCapMode(capModes[randI(0, capModes.length - 1)]!);
        setChildTrailCapFixed(randI(400, 4800));
        setChildTrailCapRandomness(Math.round(randF(0, 0.85) * 100) / 100);
        setForceMagnitude(Math.round(randF(20, 220) / 5) * 5);
        setVelocityKickGradual(Math.random() < 0.45);
        setVelocityKickBlendFrames(randI(4, 28));
        const minW = Math.round(randF(0.05, 1.5) * 100) / 100;
        const maxW = Math.max(
            minW,
            Math.round(randF(0.8, 6) * 100) / 100,
        );
        setMinStrokeWidth(minW);
        setStrokeWidth(maxW);
        setInitialStrokeWidth(
            Math.round(randF(minW, Math.min(maxW, 4)) * 100) / 100,
        );
        setStrokeWidthStep(Math.round(randF(0.1, 2) * 100) / 100);
        setStrokeWidthKickFrames(randI(0, 45));
        setStrokeWidthKickGradual(Math.random() < 0.35);
        setStrokeWidthBlendFrames(randI(4, 28));
        setSmoothPaths(Math.random() < 0.35);
        setMergeTrailToSinglePath(Math.random() < 0.75);
        setFilledVariableWidthStroke(Math.random() < 0.18);
        setUnionGroupOnComplete(true);
        setStrokeColor(
            `#${randomHexByte(22, 92)}${randomHexByte(22, 92)}${randomHexByte(22, 92)}`,
        );
        setBackgroundColor(
            `#${randomHexByte(244, 255)}${randomHexByte(242, 255)}${randomHexByte(238, 255)}`,
        );
    }, []);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            width, height, marginFactor, cellSize, seed, angleNoiseMode,
            angleNoiseOctaves, angleWarpStrength, angleWarpFrequency,
            noiseScale, stepNoiseScale, stepVariation, angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX, mirrorNoiseY, uniformFlowBlend, uniformFlowAngleDeg,
            followSpeed, flowInfluence, maxTrailLength, maxTrailLengthRandomness,
            strokeColor, strokePaletteText, randomStrokeColorPerAgent,
            strokeWidth, minStrokeWidth, initialStrokeWidth,
            strokeWidthStep, strokeWidthKickFrames, strokeWidthKickGradual,
            strokeWidthBlendFrames, trailTaperStartLength, trailTaperEndLength,
            smoothPaths,
            mergeTrailToSinglePath, filledVariableWidthStroke, unionGroupOnComplete,
            backgroundColor, exportBackground, groupSize, groupRadius,
            strokeLayoutCount, strokeLayoutSpacing, groupCohesionStrength,
            kickIntervalSec, kickIntervalRandomnessSec,
            spawnChildIntervalSec, spawnChildIntervalRandomnessSec,
            childTrailCapMode, childTrailCapFixed, childTrailCapRandomness,
            stopAtMargin,
            forceMagnitude, velocityKickGradual, velocityKickBlendFrames,
            inputMode,
        }),
        [
            width, height, marginFactor, cellSize, seed, angleNoiseMode,
            angleNoiseOctaves, angleWarpStrength, angleWarpFrequency,
            noiseScale, stepNoiseScale, stepVariation, angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX, mirrorNoiseY, uniformFlowBlend, uniformFlowAngleDeg,
            followSpeed, flowInfluence, maxTrailLength, maxTrailLengthRandomness,
            strokeColor, strokePaletteText, randomStrokeColorPerAgent,
            strokeWidth, minStrokeWidth, initialStrokeWidth,
            strokeWidthStep, strokeWidthKickFrames, strokeWidthKickGradual,
            strokeWidthBlendFrames, trailTaperStartLength, trailTaperEndLength,
            smoothPaths,
            mergeTrailToSinglePath, filledVariableWidthStroke, unionGroupOnComplete,
            backgroundColor, exportBackground, groupSize, groupRadius,
            strokeLayoutCount, strokeLayoutSpacing, groupCohesionStrength,
            kickIntervalSec, kickIntervalRandomnessSec,
            spawnChildIntervalSec, spawnChildIntervalRandomnessSec,
            childTrailCapMode, childTrailCapFixed, childTrailCapRandomness,
            stopAtMargin,
            forceMagnitude, velocityKickGradual, velocityKickBlendFrames,
            inputMode,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.width != null) setWidth(values.width as number);
        if (values.height != null) setHeight(values.height as number);
        if (values.marginFactor != null) setMarginFactor(values.marginFactor as number);
        if (values.cellSize != null) setCellSize(values.cellSize as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (
            values.angleNoiseMode === "perlin" ||
            values.angleNoiseMode === "fbm" ||
            values.angleNoiseMode === "domain_warp"
        ) {
            setAngleNoiseMode(values.angleNoiseMode);
        }
        if (values.angleNoiseOctaves != null) {
            setAngleNoiseOctaves(values.angleNoiseOctaves as number);
        }
        if (values.angleWarpStrength != null) {
            setAngleWarpStrength(values.angleWarpStrength as number);
        }
        if (values.angleWarpFrequency != null) {
            setAngleWarpFrequency(values.angleWarpFrequency as number);
        }
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.stepNoiseScale != null) setStepNoiseScale(values.stepNoiseScale as number);
        if (values.stepVariation != null) setStepVariation(values.stepVariation as number);
        if (values.angleQuantizeDeg != null) setAngleQuantizeDeg(values.angleQuantizeDeg as number);
        if (values.fieldRotationDeg != null) setFieldRotationDeg(values.fieldRotationDeg as number);
        if (values.mirrorNoiseX != null) setMirrorNoiseX(values.mirrorNoiseX as boolean);
        if (values.mirrorNoiseY != null) setMirrorNoiseY(values.mirrorNoiseY as boolean);
        if (values.uniformFlowBlend != null) setUniformFlowBlend(values.uniformFlowBlend as number);
        if (values.uniformFlowAngleDeg != null) setUniformFlowAngleDeg(values.uniformFlowAngleDeg as number);
        if (values.followSpeed != null) setFollowSpeed(values.followSpeed as number);
        if (values.flowInfluence != null) setFlowInfluence(values.flowInfluence as number);
        if (values.maxTrailLength != null) setMaxTrailLength(values.maxTrailLength as number);
        if (values.maxTrailLengthRandomness != null) setMaxTrailLengthRandomness(values.maxTrailLengthRandomness as number);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.strokePaletteText != null) {
            setStrokePaletteText(values.strokePaletteText as string);
        }
        if (values.randomStrokeColorPerAgent != null) {
            setRandomStrokeColorPerAgent(values.randomStrokeColorPerAgent as boolean);
        }
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.minStrokeWidth != null) setMinStrokeWidth(values.minStrokeWidth as number);
        if (values.initialStrokeWidth != null) setInitialStrokeWidth(values.initialStrokeWidth as number);
        if (values.strokeWidthStep != null) setStrokeWidthStep(values.strokeWidthStep as number);
        if (values.strokeWidthKickFrames != null) setStrokeWidthKickFrames(values.strokeWidthKickFrames as number);
        if (values.strokeWidthKickGradual != null) setStrokeWidthKickGradual(values.strokeWidthKickGradual as boolean);
        if (values.strokeWidthBlendFrames != null) setStrokeWidthBlendFrames(values.strokeWidthBlendFrames as number);
        if (values.trailTaperStartLength != null) setTrailTaperStartLength(values.trailTaperStartLength as number);
        if (values.trailTaperEndLength != null) setTrailTaperEndLength(values.trailTaperEndLength as number);
        if (values.smoothPaths != null) setSmoothPaths(values.smoothPaths as boolean);
        if (values.mergeTrailToSinglePath != null) setMergeTrailToSinglePath(values.mergeTrailToSinglePath as boolean);
        if (values.filledVariableWidthStroke != null) setFilledVariableWidthStroke(values.filledVariableWidthStroke as boolean);
        if (values.unionGroupOnComplete != null) setUnionGroupOnComplete(values.unionGroupOnComplete as boolean);
        if (values.backgroundColor != null) setBackgroundColor(values.backgroundColor as string);
        if (values.exportBackground != null) setExportBackground(values.exportBackground as boolean);
        if (values.groupSize != null) setGroupSize(values.groupSize as number);
        if (values.groupRadius != null) setGroupRadius(values.groupRadius as number);
        if (values.strokeLayoutCount != null) setStrokeLayoutCount(values.strokeLayoutCount as number);
        if (values.strokeLayoutSpacing != null) setStrokeLayoutSpacing(values.strokeLayoutSpacing as number);
        if (values.groupCohesionStrength != null) setGroupCohesionStrength(values.groupCohesionStrength as number);
        if (values.kickIntervalSec != null) setKickIntervalSec(values.kickIntervalSec as number);
        if (values.kickIntervalRandomnessSec != null) setKickIntervalRandomnessSec(values.kickIntervalRandomnessSec as number);
        if (values.spawnChildIntervalSec != null) setSpawnChildIntervalSec(values.spawnChildIntervalSec as number);
        if (values.spawnChildIntervalRandomnessSec != null) setSpawnChildIntervalRandomnessSec(values.spawnChildIntervalRandomnessSec as number);
        if (
            values.childTrailCapMode === "none" ||
            values.childTrailCapMode === "parentRemaining" ||
            values.childTrailCapMode === "fixed"
        ) {
            setChildTrailCapMode(values.childTrailCapMode);
        } else if (values.childTrailCapFromParentRemaining === true) {
            setChildTrailCapMode("parentRemaining");
        } else if (values.childTrailCapFromParentRemaining === false) {
            setChildTrailCapMode("none");
        }
        if (values.childTrailCapFixed != null) {
            setChildTrailCapFixed(values.childTrailCapFixed as number);
        }
        if (values.childTrailCapRandomness != null) {
            setChildTrailCapRandomness(values.childTrailCapRandomness as number);
        }
        if (values.stopAtMargin != null) setStopAtMargin(values.stopAtMargin as boolean);
        if (values.forceMagnitude != null) setForceMagnitude(values.forceMagnitude as number);
        if (values.velocityKickGradual != null) setVelocityKickGradual(values.velocityKickGradual as boolean);
        if (values.velocityKickBlendFrames != null) setVelocityKickBlendFrames(values.velocityKickBlendFrames as number);
        if (values.inputMode === "clickSpawn" || values.inputMode === "dragPaint") {
            setInputMode(values.inputMode);
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "flow-agents",
        defaultPreset: DEFAULT_FLOW_AGENTS_PRESET,
        getPresetValues,
        applyPreset,
    });

    const value: FlowAgentsState = {
        width,
        height,
        marginFactor,
        cellSize,
        seed,
        angleNoiseMode,
        angleNoiseOctaves,
        angleWarpStrength,
        angleWarpFrequency,
        noiseScale,
        stepNoiseScale,
        stepVariation,
        angleQuantizeDeg,
        fieldRotationDeg,
        mirrorNoiseX,
        mirrorNoiseY,
        uniformFlowBlend,
        uniformFlowAngleDeg,
        showFlowField,
        followSpeed,
        flowInfluence,
        maxTrailLength,
        maxTrailLengthRandomness,
        strokeColor,
        strokePaletteText,
        setStrokePaletteText,
        randomStrokeColorPerAgent,
        setRandomStrokeColorPerAgent,
        strokeWidth,
        minStrokeWidth,
        initialStrokeWidth,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        trailTaperStartLength,
        trailTaperEndLength,
        smoothPaths,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        unionGroupOnComplete,
        backgroundColor,
        exportBackground,
        groupSize,
        groupRadius,
        strokeLayoutCount,
        strokeLayoutSpacing,
        groupCohesionStrength,
        kickIntervalSec,
        kickIntervalRandomnessSec,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        stopAtMargin,
        forceMagnitude,
        velocityKickGradual,
        velocityKickBlendFrames,
        inputMode,
        setInputMode,
        setPuppetBrushTarget,
        clearPuppetBrush,
        setWidth,
        setHeight,
        setMarginFactor,
        setCellSize,
        setSeed,
        setAngleNoiseMode,
        setAngleNoiseOctaves,
        setAngleWarpStrength,
        setAngleWarpFrequency,
        setNoiseScale,
        setStepNoiseScale,
        setStepVariation,
        setAngleQuantizeDeg,
        setFieldRotationDeg,
        setMirrorNoiseX,
        setMirrorNoiseY,
        setUniformFlowBlend,
        setUniformFlowAngleDeg,
        setShowFlowField,
        setFollowSpeed,
        setFlowInfluence,
        setMaxTrailLength,
        setMaxTrailLengthRandomness,
        setStrokeColor,
        setStrokeWidth,
        setMinStrokeWidth,
        setInitialStrokeWidth,
        setStrokeWidthStep,
        setStrokeWidthKickFrames,
        setStrokeWidthKickGradual,
        setStrokeWidthBlendFrames,
        setTrailTaperStartLength,
        setTrailTaperEndLength,
        setSmoothPaths,
        setMergeTrailToSinglePath,
        setFilledVariableWidthStroke,
        setUnionGroupOnComplete,
        setBackgroundColor,
        setExportBackground,
        setGroupSize,
        setGroupRadius,
        setStrokeLayoutCount,
        setStrokeLayoutSpacing,
        setGroupCohesionStrength,
        setKickIntervalSec,
        setKickIntervalRandomnessSec,
        setSpawnChildIntervalSec,
        setSpawnChildIntervalRandomnessSec,
        setChildTrailCapMode,
        setChildTrailCapFixed,
        setChildTrailCapRandomness,
        setStopAtMargin,
        setForceMagnitude,
        setVelocityKickGradual,
        setVelocityKickBlendFrames,
        viewBoxAttr,
        debugSegments,
        activeAgents,
        completedPaths,
        completedUnionGroups,
        spawnAgentAt,
        clearTrails,
        canExport,
        buildExportSvgString,
        handleDownload,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <FlowAgentsContext.Provider value={value}>
            {children}
        </FlowAgentsContext.Provider>
    );
}
